import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAddCashflow, useUpdateCashflow } from "../../hooks/investments";
import type { CashflowDto, InvestmentCashflowType } from "../../lib/investments";
import { MoneyInput } from "../forms/MoneyInput";
import { Modal } from "../ui/Modal";

const CASHFLOW_TYPES: InvestmentCashflowType[] = ["DEPOSIT", "WITHDRAW", "INTEREST"];

function todayIso(): string {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
    .toISOString()
    .slice(0, 10);
}

interface Props {
  investmentId: string;
  existing?: CashflowDto;
  onClose: () => void;
}

export function CashflowFormModal({ investmentId, existing, onClose }: Props) {
  const { t } = useTranslation();
  const addMutation = useAddCashflow(investmentId);
  const updateMutation = useUpdateCashflow(investmentId);

  const [date, setDate] = useState(existing?.date ?? todayIso());
  const [type, setType] = useState<InvestmentCashflowType>(existing?.type ?? "DEPOSIT");
  const [amountCents, setAmountCents] = useState<number | null>(existing?.amountCents ?? null);
  const [notes, setNotes] = useState(existing?.notes ?? "");
  const [error, setError] = useState<string | null>(null);

  const isEdit = existing !== undefined;
  const title = isEdit
    ? t("investments.cashflow.edit_button")
    : t("investments.cashflow.add_button");

  useEffect(() => {
    setDate(existing?.date ?? todayIso());
    setType(existing?.type ?? "DEPOSIT");
    setAmountCents(existing?.amountCents ?? null);
    setNotes(existing?.notes ?? "");
    setError(null);
  }, [existing]);

  const submitting = addMutation.isPending || updateMutation.isPending;

  async function handleSubmit() {
    setError(null);
    if (amountCents === null || amountCents <= 0) {
      setError(t("investments.errors.invalid_amount"));
      return;
    }
    try {
      if (isEdit && existing) {
        await updateMutation.mutateAsync({
          id: existing.id,
          patch: {
            date,
            type,
            amountCents,
            notes: notes.trim() === "" ? null : notes.trim(),
          },
        });
      } else {
        const input = {
          date,
          type,
          amountCents,
          notes: notes.trim() === "" ? undefined : notes.trim(),
        };
        await addMutation.mutateAsync(input);
      }
      onClose();
    } catch {
      setError(t("investments.errors.validation"));
    }
  }

  return (
    <Modal open onClose={onClose} title={title}>
      <div className="space-y-3">
        {isEdit && existing?.source === "IMPORT" && (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            {t("investments.cashflow.edit_import_warning")}
          </div>
        )}
        <label className="block">
          <span className="text-sm">{t("investments.cashflow.form_date_label")}</span>
          <input
            type="date"
            className="block mt-1 w-full border rounded px-2 py-1"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
          />
        </label>
        <label className="block">
          <span className="text-sm">{t("investments.cashflow.form_type_label")}</span>
          <select
            className="block mt-1 w-full border rounded px-2 py-1"
            value={type}
            onChange={(e) => setType(e.target.value as InvestmentCashflowType)}
          >
            {CASHFLOW_TYPES.map((ct) => (
              <option key={ct} value={ct}>
                {t(`investments.cashflow.type_${ct}`)}
              </option>
            ))}
          </select>
        </label>
        <div className="space-y-1">
          <label htmlFor="cf-amount" className="text-sm">
            {t("investments.cashflow.form_amount_label")}
          </label>
          <MoneyInput
            valueCents={amountCents}
            onChange={setAmountCents}
            placeholder="500,00"
            className="block mt-1 w-full border rounded px-2 py-1"
          />
        </div>
        <label className="block">
          <span className="text-sm">{t("investments.cashflow.form_notes_label")}</span>
          <input
            type="text"
            className="block mt-1 w-full border rounded px-2 py-1"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={500}
          />
        </label>
        {error && <div className="text-red-700 text-sm">{error}</div>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1 border rounded">
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="px-3 py-1 bg-blue-600 text-white rounded"
          >
            {submitting ? t("common.submitting") : t("common.save")}
          </button>
        </div>
      </div>
    </Modal>
  );
}
